import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { useStore, formatPrice } from "@/lib/store";
import { useState } from "react";
import { t } from "@/lib/i18n";
import { optimizeImg, srcSet } from "@/lib/img";
import StarRating from "@/components/StarRating";

export default function ProductCard({ product, index = 0, showColor = true }) {
  const { wishlist, toggleWishlist } = useStore();
  const [hover, setHover] = useState(false);
  const inWishlist = wishlist.includes(product.handle);
  const primary = product.images[0];
  const secondary = product.images[1] || product.images[0];

  return (
    <Link
      to={`/products/${product.handle}`}
      data-testid={`product-card-${product.handle}`}
      className="group block"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className="relative overflow-hidden bg-[color:var(--marcini-bg-warm)] aspect-[3/4]">
        <img
          src={optimizeImg(primary, 800)}
          srcSet={srcSet(primary, [400, 800, 1200])}
          sizes="(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, 50vw"
          alt={product.title}
          loading={index < 3 ? "eager" : "lazy"}
          className={`w-full h-full object-cover transition-opacity duration-700 ${hover && secondary !== primary ? "opacity-0" : "opacity-100"}`}
        />
        {secondary !== primary && (
          <img
            src={optimizeImg(secondary, 800)}
            srcSet={srcSet(secondary, [400, 800, 1200])}
            sizes="(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, 50vw"
            alt=""
            aria-hidden
            loading="lazy"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${hover ? "opacity-100" : "opacity-0"}`}
          />
        )}

        <button
          type="button"
          data-testid={`wishlist-toggle-${product.handle}`}
          onClick={(e) => { e.preventDefault(); toggleWishlist(product.handle); }}
          className={`absolute top-3 right-3 w-9 h-9 flex items-center justify-center bg-white/70 backdrop-blur-md border border-white/40 hover:bg-white transition-colors z-10`}
          aria-label={t.pdp.saveToWishlist}
        >
          <Heart size={15} className={inWishlist ? "fill-black text-black" : "text-black"} />
        </button>

        {!product.available && (
          <span
            data-testid={`sold-out-${product.handle}`}
            className="absolute top-3 left-3 bg-white text-black text-[11px] tracking-[0.28em] font-medium px-3 py-1.5 border-l-2 border-[#c8102e] shadow-sm"
          >
            {t.common.soldOut.toUpperCase()}
          </span>
        )}
      </div>

      <div className="mt-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-serif-editorial text-lg md:text-xl leading-tight">{product.title}</p>
          {showColor && product.color_name && product.color_name !== product.title && (
            <p className="text-eyebrow text-[color:var(--marcini-fg-3)] mt-1 truncate">{product.color_name}</p>
          )}
          <StarRating handle={product.handle} size={11} className="mt-2" />
        </div>
        <p className={`text-sm mt-1 whitespace-nowrap ${!product.available ? "line-through text-[color:var(--marcini-fg-3)]" : ""}`}>{formatPrice(product.price_range.min)}</p>
      </div>
    </Link>
  );
}
